import { JSONContent } from '@tiptap/core';

import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { SimpleCard } from 'approot/shared/simple-card/simple-card';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { ActivityBuilderIntroPreview } from '../page/activity-builder-page-preview';
import { ACTIVITY_BUILDER_SLIDE_RATIO } from '../activity-builder.constants';
import {
  NotificationContent,
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import { purple1 } from 'approot/shared/styles/colours.style';
import { useCsInSchool } from 'approot/shared/authors/authors.hooks';
import { CsInSchoolsLink } from 'approot/shared/authors/cs-in-schools';
import NativeFeedbackSurvey from 'approot/shared/feedback-survey/native-feedback-survey';
import { BaseFeedbackProps } from 'approot/shared/feedback-survey/native-feedback-survey.constants';
import CompleteModalButton from 'approot/completion/complete-modal-button';
import {
  COMPLETION_STATUS_COMPLETED,
  CONTENT_OBJECT_TYPE_LESSON,
} from 'global/constants';
import type { CompletionFragment } from '../../planner/manage/planner.graphql.generated';
import { Box } from 'approot/shared/design-system/box/box.style';
import IconCircleCheck from 'approot/shared/icons/icon-circle-check';
import * as Styled from './activity-builder-conclusion.style';

import imgPath from './check.png';
import type { Pluck } from 'lib/gql/pluck';

export type CompletionProps = {
  unitId: number;
  isInPlanner: boolean;
  completion: CompletionFragment | null;
};

const ActivityBuilderConclusionSlide = ({
  exitLink,
  exitLabel,
  contentName,
  focusQuestion,
  phenomena,
  assessmentName,
  responsiveImages,
  authorId,
  feedbackProps,
  completionProps,
}: {
  exitLink: string;
  exitLabel: string;
  contentName: string;
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  assessmentName?: JSONContent | null;
  responsiveImages: ResponsiveImage[];
  authorId: number;
  feedbackProps?: BaseFeedbackProps;
  completionProps?: CompletionProps;
}) => {
  const { registerHeading } = useAccessibleHeading();
  const { isCsInSchoolsLesson } = useCsInSchool(authorId);

  return (
    <Styled.ConclusionView>
      <ScreenReaderOnly as="h1" tabIndex={-1} ref={registerHeading}>
        {`Conclusion slide for ${contentName}`}
      </ScreenReaderOnly>
      <Styled.ConclusionWrapper aria-hidden $isSmall={isCsInSchoolsLesson}>
        <ConclusionContentImage
          focusQuestion={focusQuestion}
          phenomena={phenomena}
          assessmentName={assessmentName}
          responsiveImages={responsiveImages}
        />
        {completionProps ? (
          completionProps.completion?.status === COMPLETION_STATUS_COMPLETED ? (
            <ConclusionCompletionCheck />
          ) : null
        ) : (
          <ConclusionCompletionCheck />
        )}
      </Styled.ConclusionWrapper>

      {isCsInSchoolsLesson && <CSInSchoolsConclusion />}

      {completionProps && (
        <Box $marginBottom={16}>
          <CompleteModalButton
            contentId={completionProps.completion?.contentId || 0}
            contentName={contentName}
            unitId={completionProps.unitId}
            isLesson={
              completionProps.completion?.contentType ===
              CONTENT_OBJECT_TYPE_LESSON
            }
            isInPlanner={completionProps.isInPlanner}
            completion={completionProps.completion}
            renderButton={({ onClick, isComplete }) =>
              isComplete ? (
                <Styled.ConclusionIncompleteButton
                  type="button"
                  $isLarge
                  $borderRadius={40}
                  onClick={onClick}
                >
                  <IconCircleCheck width={24} height={24} /> Completed
                </Styled.ConclusionIncompleteButton>
              ) : (
                <Styled.ConclusionCompleteButton
                  type="button"
                  $borderRadius={40}
                  $isLarge
                  onClick={onClick}
                >
                  <IconCircleCheck width={24} height={24} /> Mark Complete
                </Styled.ConclusionCompleteButton>
              )
            }
          />
        </Box>
      )}

      {feedbackProps ? (
        <NativeFeedbackSurvey
          feature={feedbackProps.feature}
          contentName={feedbackProps.contentName}
          contentType={feedbackProps.contentType}
          contentId={feedbackProps.contentId}
          classId={feedbackProps.classId}
          exitLink={exitLink}
          exitLabel={exitLabel}
        />
      ) : (
        <Styled.ConclusionButton to={exitLink}>
          {exitLabel}
        </Styled.ConclusionButton>
      )}
    </Styled.ConclusionView>
  );
};

export default ActivityBuilderConclusionSlide;

export const ConclusionContentImage = ({
  focusQuestion,
  phenomena,
  assessmentName,
  responsiveImages,
}: {
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  assessmentName?: JSONContent | null;
  responsiveImages: ResponsiveImage[];
}) => {
  return (
    <SimpleCard
      ariaTitle="Conclusion"
      imageRatio={ACTIVITY_BUILDER_SLIDE_RATIO}
      bodyText={
        <ActivityBuilderIntroPreview
          focusQuestion={focusQuestion}
          phenomena={phenomena}
          assessmentName={assessmentName}
          introBackground={responsiveImages?.[1]?.urls?.[0].url}
        />
      }
    />
  );
};

export const ConclusionCompletionCheck = () => {
  return (
    <Styled.ConclusionCheck>
      <img src={imgPath} alt="" />
    </Styled.ConclusionCheck>
  );
};

export const CSInSchoolsConclusion = () => {
  return (
    <NotificationTip $colour={purple1}>
      <NotificationContent>
        <NotificationTipText marginBottom={0}>
          Created in partnership with <CsInSchoolsLink />
        </NotificationTipText>
      </NotificationContent>
    </NotificationTip>
  );
};
